import { useConvexAction, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { Bold, Italic, Link2, List, ListOrdered } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TranslateButton } from '@/features/jobs/components/translate-button'
import { GenerateButton } from '@/shared/ui/generate-button'

interface IntroOutroEditorProps {
  readonly jobId: Id<'jobs'>
  readonly field: 'introMessage' | 'outroMessage'
  readonly initialValue: string | null | undefined
  readonly jobLanguage: 'sv' | 'en' | 'both' | 'no' | 'da' | 'fi'
  readonly initialTranslation: string | null | undefined
  readonly jobTitle: string
  readonly jobDescription: string
}

function SingleEditor({
  placeholder,
  initialContent,
  onSave,
  isDraft,
}: {
  readonly placeholder: string
  readonly initialContent: string
  readonly onSave: (html: string) => void
  readonly isDraft: boolean
}) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: initialContent,
    onBlur: ({ editor: e }) => {
      const html = e.getHTML()
      onSave(html === '<p></p>' ? '' : html)
    },
  })

  const handleSetLink = (): void => {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('URL', previousUrl)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  if (!editor) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1 rounded-md border border-border bg-secondary p-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('bold') ? 'bg-primary/10' : ''}`}
          aria-label="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('italic') ? 'bg-primary/10' : ''}`}
          aria-label="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('bulletList') ? 'bg-primary/10' : ''}`}
          aria-label="Bullet list"
        >
          <List className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('orderedList') ? 'bg-primary/10' : ''}`}
          aria-label="Numbered list"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleSetLink}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('link') ? 'bg-primary/10' : ''}`}
          aria-label="Link"
        >
          <Link2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <EditorContent
        editor={editor}
        className={`min-h-24 rounded-md border bg-card px-3 py-2 text-sm text-primary focus-within:ring-1 focus-within:ring-primary/30 [&_.tiptap]:outline-none [&_.tiptap_a]:underline [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-4 [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-4 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:text-primary/30 [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] ${isDraft ? 'border-amber-400 ring-1 ring-amber-400/30' : 'border-border'}`}
      />
    </div>
  )
}

export function IntroOutroEditor({
  jobId,
  field,
  initialValue,
  jobLanguage,
  initialTranslation,
  jobTitle,
  jobDescription,
}: IntroOutroEditorProps) {
  const { t } = useTranslation()
  const [translationDraft, setTranslationDraft] = useState<string | null>(null)
  const [primaryContent, setPrimaryContent] = useState(initialValue ?? '')
  const [primaryKey, setPrimaryKey] = useState(0)

  const { mutate: save } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const { mutate: saveTranslation } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.jobs.saveMyJobFieldTranslation,
    ),
  })

  const { mutateAsync: generateMessage, isPending: isGenerating } = useMutation(
    {
      mutationFn: useConvexAction(
        api.features.jobs.generate_text_fields.generateMyIntroOutroMessage,
      ),
    },
  )

  const primaryPlaceholder =
    field === 'introMessage'
      ? t('jobs.settings.introMessagePlaceholder')
      : t('jobs.settings.outroMessagePlaceholder')

  const handleGenerate = (): void => {
    void generateMessage({ jobId, field, jobTitle, jobDescription }).then(
      (html) => {
        setPrimaryContent(html)
        setPrimaryKey((k) => k + 1)
      },
    )
  }

  if (jobLanguage !== 'both') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-end">
          <GenerateButton
            isPending={isGenerating}
            disabled={!jobTitle.trim()}
            onClick={handleGenerate}
          />
        </div>
        <SingleEditor
          key={primaryKey}
          placeholder={primaryPlaceholder}
          initialContent={primaryContent}
          onSave={(html) => save({ jobId, [field]: html })}
          isDraft={false}
        />
      </div>
    )
  }

  const translationContent = translationDraft ?? initialTranslation ?? ''

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-primary/60">
            {t('jobs.translations.swedish')}
          </span>
          <GenerateButton
            isPending={isGenerating}
            disabled={!jobTitle.trim()}
            onClick={handleGenerate}
          />
        </div>
        <SingleEditor
          key={primaryKey}
          placeholder={primaryPlaceholder}
          initialContent={primaryContent}
          onSave={(html) => save({ jobId, [field]: html })}
          isDraft={false}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-primary/60">
            {t('jobs.translations.english')}
          </span>
          <TranslateButton
            jobId={jobId}
            sourceLanguage="sv"
            targetLanguage="en"
            text={primaryContent}
            isHtml={true}
            disabled={!primaryContent.trim() || primaryContent === '<p></p>'}
            onTranslated={(text) => setTranslationDraft(text)}
          />
        </div>
        {translationDraft !== null && (
          <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            {t('jobs.translations.draftHint')}
          </div>
        )}
        <SingleEditor
          placeholder={primaryPlaceholder}
          initialContent={translationContent}
          onSave={(html) => {
            setTranslationDraft(null)
            saveTranslation({ jobId, field, language: 'en', value: html })
          }}
          isDraft={translationDraft !== null}
        />
      </div>
    </div>
  )
}
